import { useState, useEffect, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { trackPhoneConversion } from '../../utils/analytics';

const GRADIENT = 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)';

export const gradientTextStyle: React.CSSProperties = {
  background: GRADIENT,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

export interface CTAButton {
  label: string;
  icon?: string;
  href?: string;
  to?: string;
  onClick?: () => void;
  variant: 'primary' | 'secondary';
}

interface PageHeroProps {
  label: string;
  headline: ReactNode;
  subtext: string;
  backgroundImage: string;
  backgroundPosition?: string;
  overlayClasses?: string;
  ctas?: CTAButton[];
  children?: ReactNode;
}

export default function PageHero({
  label,
  headline,
  subtext,
  backgroundImage,
  backgroundPosition = 'center',
  overlayClasses = 'from-black/75 via-black/55 to-[#0e0e0e]',
  ctas = [],
  children,
}: PageHeroProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const anim = (delay: number): React.CSSProperties => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(40px)',
    transition: `opacity 1s ease ${delay}s, transform 1s ease ${delay}s`,
  });

  const trackCtaClick = (label: string) => {
    window.dataLayer = window.dataLayer || [];
    (window.dataLayer as any[]).push({
      event: 'cta_click',
      cta_type: 'termin_buchen',
      cta_label: label,
    });
  };

  const renderButtons = () =>
    ctas.map((cta, idx) => {
      const isPrimary = cta.variant === 'primary';
      const cls =
        'flex items-center justify-center gap-2 px-7 sm:px-10 py-4 sm:py-5 font-bold text-sm sm:text-base rounded-xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto';
      const primaryStyle: React.CSSProperties = {
        background: GRADIENT,
        color: '#FFFFFF',
        boxShadow: '0 10px 30px rgba(16,185,129,0.3)',
      };
      const secondaryStyle: React.CSSProperties = {
        backgroundColor: 'rgba(255,255,255,0.12)',
        color: '#FFFFFF',
        border: '2px solid rgba(255,255,255,0.35)',
        backdropFilter: 'blur(8px)',
      };
      const btnStyle = isPrimary ? primaryStyle : secondaryStyle;
      const inner = (
        <>
          {cta.icon && <i className={`${cta.icon} text-sm sm:text-base`}></i>}
          {cta.label}
        </>
      );

      if (cta.href) {
        const isTel = cta.href.startsWith('tel:');
        return (
          <a
            key={idx}
            href={cta.href}
            className="w-full sm:w-auto"
            onClick={isTel ? (e: React.MouseEvent) => { e.preventDefault(); trackPhoneConversion(cta.href!); } : undefined}
          >
            <button className={cls} style={btnStyle}>{inner}</button>
          </a>
        );
      }
      if (cta.to) {
        const isTermin = cta.to === '/termin-buchen';
        return (
          <Link
            key={idx}
            to={cta.to}
            className="w-full sm:w-auto"
            onClick={isTermin ? () => trackCtaClick(cta.label) : undefined}
          >
            <button className={cls} style={btnStyle}>{inner}</button>
          </Link>
        );
      }
      return (
        <button key={idx} onClick={cta.onClick} className={cls} style={btnStyle}>
          {inner}
        </button>
      );
    });

  return (
    <>
      {/* ── MOBILE LAYOUT (below sm) ───────────────────────────── */}
      <section
        className="block sm:hidden bg-[#0e0e0e] overflow-hidden"
        style={{ height: '100svh', paddingTop: '52px' }}
      >
        <div className="flex flex-col" style={{ height: 'calc(100svh - 52px)' }}>

          {/* Image — top 45% */}
          <div className="relative flex-none" style={{ height: '45%' }}>
            <img
              src={backgroundImage}
              alt=""
              role="presentation"
              className="w-full h-full object-cover"
              style={{ objectPosition: backgroundPosition }}
            />
            <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-[#0e0e0e] to-transparent" />
          </div>

          {/* Content — remaining space, scrollable if needed */}
          <div className="flex-1 overflow-y-auto px-5 pt-5 pb-6">
            <div style={anim(0.1)}>
              <p
                className="text-xs font-semibold tracking-[0.2em] uppercase mb-3"
                style={{ color: '#14b8a6', fontFamily: "'Inter', sans-serif" }}
              >
                {label}
              </p>
            </div>

            <div style={anim(0.2)}>
              <h1
                className="text-[1.85rem] font-black text-white mb-3"
                style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}
              >
                {headline}
              </h1>
            </div>

            <div style={anim(0.3)}>
              <p className="text-sm text-gray-300 leading-relaxed mb-6">
                {subtext}
              </p>
            </div>

            {ctas.length > 0 && (
              <div className="flex flex-col gap-3" style={anim(0.4)}>
                {renderButtons()}
              </div>
            )}

            {children && (
              <div style={anim(0.5)}>
                {children}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── DESKTOP LAYOUT (sm+) ──────────────────────────────── */}
      <section className="hidden sm:flex relative min-h-screen items-end overflow-hidden bg-[#0e0e0e]">
        <div className="absolute inset-0 z-0">
          <img
            src={backgroundImage}
            alt=""
            role="presentation"
            className="w-full h-full object-cover"
            style={{ objectPosition: backgroundPosition }}
          />
          <div className={`absolute inset-0 bg-gradient-to-b ${overlayClasses}`} />
        </div>

        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10 blur-3xl z-0 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #10b981 0%, transparent 70%)' }}
        />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-20 pt-36 lg:pt-40">
          <div className="max-w-3xl">
            <div style={anim(0.1)}>
              <p
                className="text-sm font-semibold tracking-[0.2em] uppercase mb-8"
                style={{ color: '#14b8a6', fontFamily: "'Inter', sans-serif" }}
              >
                {label}
              </p>
            </div>

            <div style={anim(0.25)}>
              <h1
                className="text-5xl lg:text-7xl xl:text-8xl font-black text-white mb-8"
                style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.05 }}
              >
                {headline}
              </h1>
            </div>

            <div style={anim(0.4)}>
              <p className="text-base lg:text-lg text-gray-300 leading-relaxed max-w-xl mb-12">
                {subtext}
              </p>
            </div>

            {ctas.length > 0 && (
              <div className="flex flex-row gap-4" style={anim(0.55)}>
                {renderButtons()}
              </div>
            )}

            {children && (
              <div style={anim(0.7)}>
                {children}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
